import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { Tab } from "react-bootstrap";
// import Papa from "papaparse";
import { leaderboards } from "src/api/poker";
import Hero from "src/components/Hero/Hero";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import SearchablePaginatedTable from "src/components/Table/SearchablePaginatedTable";
import HashTabs from "src/components/Tabbed/HashTabs";
import MarkdownWithRouter from "src/components/MarkdownWithRouter";

const SHOW_WOMEN = false;

const TournamentLeaderboard = () => {
  const { t, i18n } = useTranslation();
  // const [jsonData, setJsonData] = useState([]);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [womenLeaderboardData, setWomenLeaderboardData] = useState(null);

  // Get hash keys from translations (MUST be before useState)
  const MAIN_KEY = t("poker.tlb.mainLeaderboard.hash");
  const WOMEN_KEY = t("poker.tlb.womenLeaderboard.hash");

  // Initialize from hash on first render
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== "undefined") {
      const h = window.location.hash?.slice(1);
      if (h) return h; // honor incoming #hash
    }
    return MAIN_KEY; // default when no hash
  });

  const prevLangRef = useRef(i18n.language);

  // const handleFileUpload = (event) => {
  //   const file = event.target.files[0];
  //   const reader = new FileReader();
  //   reader.onload = () => {
  //     const csvData = reader.result;
  //     const parsedData = Papa.parse(csvData, { header: true }).data;
  //     setJsonData(parsedData);
  //   };

  //   reader.readAsText(file);
  // };

  useEffect(() => {
    const fetchLeaderboards = async () => {
      try {
        const tlbData = await leaderboards("tlb");
        setLeaderboardData(tlbData);
        if (SHOW_WOMEN) {
          const womenData = await leaderboards("tlb-women");
          setWomenLeaderboardData(womenData);
        }
      } catch (error) {
        console.error("Failed to fetch leaderboard data:", error);
      }
    };

    fetchLeaderboards();
  }, []);

  // Listen to hash changes (user clicks link with #hash)
  useEffect(() => {
    const handleHashChange = () => {
      const h = window.location.hash?.slice(1);
      const validKeys = SHOW_WOMEN ? [MAIN_KEY, WOMEN_KEY] : [MAIN_KEY];
      const nextTab = validKeys.includes(h) ? h : MAIN_KEY;
      setActiveTab(nextTab);

      if (h && !validKeys.includes(h)) {
        window.history.replaceState(null, "", `#${MAIN_KEY}`);
      }

      // Scroll to the tabs container after tab content renders
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const tabsElement = document.getElementById("tlb-tabs");
          if (tabsElement) {
            const navElement = document.getElementById("header-navbar");
            const navOffset = navElement?.offsetHeight || 0;
            const yPosition = tabsElement.getBoundingClientRect().top + window.scrollY - navOffset;
            window.scrollTo({
              top: yPosition,
              behavior: "smooth",
            });
          }
        });
      });
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [MAIN_KEY]);

  // Handle language switching and preserve tab state
  useEffect(() => {
    const prevLang = prevLangRef.current;
    if (prevLang === i18n.language) return;

    const tPrev = i18n.getFixedT(prevLangRef.current);
    const prevMain = tPrev("poker.tlb.mainLeaderboard.hash");
    const prevWomen = tPrev("poker.tlb.womenLeaderboard.hash");

    const nextTab =
      SHOW_WOMEN && activeTab === prevWomen ? WOMEN_KEY : MAIN_KEY;
    setActiveTab(nextTab);
    window.history.replaceState(null, "", `#${nextTab}`);

    // Scroll to tabs after language change
    setTimeout(() => {
      const tabsElement = document.getElementById("tlb-tabs");
      if (tabsElement && window.location.hash) {
        const navElement = document.getElementById("header-navbar");
        const navOffset = navElement?.offsetHeight || 0;
        const yPosition = tabsElement.getBoundingClientRect().top + window.scrollY - navOffset;
        window.scrollTo({
          top: yPosition,
          behavior: "smooth",
        });
      }
    }, 100);

    prevLangRef.current = i18n.language;
  }, [i18n.language, activeTab, MAIN_KEY, WOMEN_KEY]);

  // Handle tab selection - update state AND URL
  const handleTabSelect = (key) => {
    if (!key) return;
    setActiveTab(key);
    window.history.replaceState(null, "", "#" + key);
  };

  return (
    <>
      <MainDiv>
        <Hero
          bgColor=""
          bgImage="promotions/poker/wsopc-playground-sept-nov-tlb-2026/tlb-hero-image-2400x800px.jpg"
          buttonLink=""
          buttonText=""
          description={t("poker.tlb.dateRange")}
          descriptionBgColor=""
          image={`promotions/poker/wsopc-playground-sept-nov-tlb-2026/tlb-logo-2400x2400px-${i18n.language}.png`}
          imageAlt=""
          // mainHeroText={t("poker.tlb.tournamentLeaderboard_h1")}
          title=""
        />
        <container-div>
          <SubTitleSection subTitle={t("poker.tlb.prizeAnnouncement")} />
          <MarkdownWithRouter>{t("poker.tlb.prizeDescription")}</MarkdownWithRouter>
        </container-div>
        {/* {SHOW_WOMEN && (
          <container-div>
            <SubTitleSection subTitle={t("poker.tlb.ladiesTitle")} />
            <MarkdownWithRouter>{t("poker.tlb.ladiesDescription")}</MarkdownWithRouter>
          </container-div>
        )} */}
        <container-div class="mt-4">
          {SHOW_WOMEN ? (
            <HashTabs
              defaultActiveKey={MAIN_KEY}
              activeKey={activeTab}
              onSelect={handleTabSelect}
              tabs={[MAIN_KEY, WOMEN_KEY]}
              colorTheme="red"
              id="tlb-tabs"
            >
              <Tab
                id={MAIN_KEY}
                eventKey={MAIN_KEY}
                title={t("poker.tlb.mainLeaderboard.tab")}
              >
                <div id={MAIN_KEY}>
                  <SearchablePaginatedTable
                    headers={leaderboardData?.headers || []}
                    data={leaderboardData?.rows || []}
                    searchKeys={["player_name"]}
                    image={{
                      src: `poker/tlb/tlb_header_wsop_super_circuit_${i18n.language}.png`,
                      alt: "tlb_header_v2",
                    }}
                  />
                </div>
              </Tab>
              <Tab
                id={WOMEN_KEY}
                eventKey={WOMEN_KEY}
                title={t("poker.tlb.womenLeaderboard.tab")}
              >
                <div id={WOMEN_KEY}>
                  <SearchablePaginatedTable
                    headers={womenLeaderboardData?.headers || []}
                    data={womenLeaderboardData?.rows || []}
                    searchKeys={["player_name"]}
                    image={{
                      src: `poker/tlb/tlb_header_wsop_super_circuit_ladies_${i18n.language}.png`,
                      alt: "tlb_header_v2",
                    }}
                  />
                </div>
              </Tab>
            </HashTabs>
          ) : (
            <div id="tlb-tabs">
              <SearchablePaginatedTable
                headers={leaderboardData?.headers || []}
                data={leaderboardData?.rows || []}
                searchKeys={["player_name"]}
                image={{
                  src: `poker/tlb/tlb_header_wsop_super_circuit_${i18n.language}.png`,
                  alt: "tlb_header_v2",
                }}
              />
            </div>
          )}
          <div className="tlb_rules">
            <ReactMarkdown>{t("poker.tlb.prizeDisclaimer1")}</ReactMarkdown>
            <ReactMarkdown>{t("poker.tlb.prizeDisclaimer2")}</ReactMarkdown>
            {SHOW_WOMEN && (
              <ReactMarkdown>{t("poker.tlb.womenPrizeDisclaimer2")}</ReactMarkdown>
            )}
          </div>
          <SubTitleSection subTitle={t("poker.tlb.scoringInfoTitle")} />
          <div className="tlb_rules">
            <ReactMarkdown>
              {t("poker.tlb.scoringInfoDescription")}
            </ReactMarkdown>
            <ReactMarkdown>{t("poker.tlb.contactInfo")}</ReactMarkdown>
          </div>
        </container-div>
      </MainDiv>
    </>
  );
};
const MainDiv = styled.div`
  height: auto;
  width: 100%;
  position: relative;
  background: var(--black);
  .main-hero-text h1 {
    font-size: clamp(1rem, 4vw + 1rem, 4rem);
    padding: clamp(1rem, 4vw + 1rem, 4rem) 1rem;
  }
  h5 {
    color: var(--white);
  }
  p {
    color: var(--white);
  }
  .tlb_rules p {
    color: var(--grey);
  }
  .hero_image_container {
    padding: 2rem;
  }
`;

export default TournamentLeaderboard;
